import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/button';
import { Modal } from '../../components/modal';
import { Alert, Field, Muted, Stack, Strong, Textarea } from '../../components/ui';
import { useCreateInquiryChatMutation } from '../../services/stayService/__generated__/queries.generated';
import { stayErrorMessage } from '../StayHostPage/stayErrors';
import { inquirySummary } from '../ChatPage/stayChat';
import type { StayGuests } from './staySearchState';

/**
 * Ask the host a question before booking (spec 016 W27, B40). The dates and guests the guest
 * already picked go along as the topic. The conversation opens in the inbox and continues
 * into the booking if the guest books.
 */
export function ContactHostModal({
  listingId,
  hostName,
  checkIn,
  checkOut,
  guests,
  guestsPicked,
  isOpen,
  onClose,
}: {
  listingId: string;
  hostName: string;
  checkIn?: string;
  checkOut?: string;
  guests: StayGuests;
  /** Whether the guest chose a guest count (the URL has one); else it is not sent. */
  guestsPicked: boolean;
  isOpen: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [create, { loading }] = useCreateInquiryChatMutation();
  const topic = inquirySummary({
    checkIn,
    checkOut,
    guests: guestsPicked ? guests : undefined,
  });

  useEffect(() => {
    if (!isOpen) return;
    setMessage('');
    setError(null);
  }, [isOpen]);

  const send = async () => {
    setError(null);
    try {
      const { data } = await create({
        variables: {
          input: {
            listingId,
            message: message.trim(),
            ...(checkIn && checkOut ? { checkIn, checkOut } : {}),
            ...(guestsPicked ? { guests } : {}),
          },
        },
      });
      const chatId = data?.createInquiryChat.chatId;
      if (!chatId) throw new Error('The message could not be sent.');
      onClose();
      navigate(`/chat/${chatId}`, { state: { stay: true } });
    } catch (e) {
      setError(stayErrorMessage(e, 'The message could not be sent.'));
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Contact host" modalWidth="480px">
      <Stack $gap={6} data-testid="contact-host-modal">
        <Stack $gap={4}>
          <Strong style={{ fontSize: 20 }}>Message {hostName}</Strong>
          <Muted $size="sm">
            {topic ? `About ${topic}. ` : 'Ask about the place, the area or your dates. '}
            You will not be charged. If you book, the conversation continues in your reservation.
          </Muted>
        </Stack>
        <Field label="Your question" htmlFor="contact-host-message" required>
          <Textarea
            id="contact-host-message"
            rows={5}
            maxLength={1000}
            value={message}
            onChange={e => setMessage(e.target.value)}
            data-testid="contact-host-message"
          />
        </Field>
        {error ? (
          <Alert tone="error" title="Not sent">
            {error}
          </Alert>
        ) : null}
        <Button
          text="Send message"
          fullWidth
          isDisabled={!message.trim()}
          isLoading={loading}
          onClick={send}
          dataTestId="contact-host-send"
        />
      </Stack>
    </Modal>
  );
}
